import React, { useState, useEffect, useRef } from 'react';
import { CreditCard, Upload, Video, X, Camera, Mic, FileText, CheckCircle, UploadCloud, UserCircle, PhoneOff, ExternalLink, Database, Sparkles, ShieldAlert, MessageCircle } from 'lucide-react';
import SendWhatsAppButton from './SendWhatsAppButton';
import { connectSocket } from "../utils/socket";
import { playAiHoverSound, playAiSuccessSound } from "../utils/aiAudio";

export default function OnlineConsultation({ onClose, onOpenChat }: { onClose: () => void; onOpenChat?: () => void }) {
  const [step, setStep] = useState<"PAYMENT" | "UPLOAD" | "TERMS_CONSENT" | "WAITING" | "VIDEO_CALL">("PAYMENT");
  const [patientInfo, setPatientInfo] = useState({ name: '', phone: '', symptoms: '', age: '', nationalId: '' });
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [acceptedIraqiTerms, setAcceptedIraqiTerms] = useState(false);
  const [showEmbeddedVideo, setShowEmbeddedVideo] = useState(false);

  // ADDITIVE: Timezone alignment and 10-minutes interval selector
  const [availableSlots, setAvailableSlots] = useState<{ id: number; label: string; time: string; fullMsg: string }[]>([]);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(0);

  useEffect(() => {
    let active = true;
    const loadSlots = async () => {
      try {
        const res = await fetch("/api/appointments/available-slots");
        if (res.ok && active) {
          const data = await res.json();
          if (data && Array.isArray(data) && data.length > 0) {
            const slots = [];
            for (let i = 0; i < Math.min(3, data.length); i++) {
              const testDate = new Date(data[i].dateTime);
              const options: Intl.DateTimeFormatOptions = {
                timeZone: "Asia/Baghdad",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit"
              };
              const timeAr = testDate.toLocaleString("ar-IQ", options);
              const fullAr = testDate.toLocaleString("ar-IQ", {
                timeZone: "Asia/Baghdad",
                weekday: "long",
                month: "long",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit"
              });
              slots.push({
                id: i,
                label: i === 0 ? "الموعد الأول المقترح" : i === 1 ? "الموعد الثاني" : "الموعد الثالث",
                time: timeAr,
                fullMsg: fullAr,
                dateTime: data[i].dateTime // Add this missing prop
              });
            }
            setAvailableSlots(slots);
          }
        }
      } catch (err) {
        console.error("Error loading available slots in consultation view:", err);
      }
    };
    loadSlots();
    return () => {
      active = false;
    };
  }, []);

  // Public Telehealth FAQ Grounded Search States (Additive Only)
  const [showFAQ, setShowFAQ] = useState(false);
  const [faqQuestion, setFaqQuestion] = useState("");
  const [faqAnswer, setFaqAnswer] = useState<string | null>(null);
  const [faqSources, setFaqSources] = useState<{ title: string; url: string }[]>([]);
  const [faqLoading, setFaqLoading] = useState(false);

  const handleFetchFAQ = async (qst: string) => {
    if (!qst.trim()) return;
    setFaqQuestion(qst);
    setFaqLoading(true);
    setFaqAnswer(null);
    setFaqSources([]);
    try {
      const response = await fetch("/api/telehealth-faq", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: qst })
      });
      const data = await response.json();
      if (response.ok) {
        setFaqAnswer(data.answer);
        setFaqSources(data.sources || []);
      } else {
        setFaqAnswer(data.error || "حدث خطأ أثناء التواصل مع مركز الاستفسارات الطبية للذكاء الاصطناعي.");
      }
    } catch (err) {
      console.error("[Clinical FAQ Request Failed]:", err);
      setFaqAnswer("تعذر الحصول على معلومات نتيجة انقطاع الاتصال بالخادم. يرجى مراجعة شبكة الإنترنت لديك.");
    } finally {
      setFaqLoading(false);
    }
  };

  // Iraqi Payment Integration States
  const [paymentMethod, setPaymentMethod] = useState<"ZAIN_CASH" | "AL_TAIF_BANK" | "SUPER_QI" | "QI_CARD">("ZAIN_CASH");
  const [senderPhoneOrAccount, setSenderPhoneOrAccount] = useState("");
  const [senderName, setSenderName] = useState("");
  const [txReference, setTxReference] = useState("");
  const [paymentError, setPaymentError] = useState<string | null>(null);
  
  // Clinical Consultation State
  const [createdConsultation, setCreatedConsultation] = useState<any | null>(null);
  const [liveDocuments, setLiveDocuments] = useState<{name: string, url: string, sender: string}[]>([]);

  // Iraqi National Unified ID card database integration state (وزارة الداخلية العراقية)
  const [isVerifyingIraqiId, setIsVerifyingIraqiId] = useState(false);
  const [iraqiVerificationData, setIraqiVerificationData] = useState<any | null>(null);
  const [iraqiVerificationError, setIraqiVerificationError] = useState<string | null>(null);
  const [nameValidationError, setNameValidationError] = useState<string | null>(null);

  // Iraqi joint telecoms subscriber registry state (دليل هواتف العراق المشترك العراقي - زين، آسيا سيل، كورك)
  const [isVerifyingIraqiPhone, setIsVerifyingIraqiPhone] = useState(false);
  const [iraqiPhoneData, setIraqiPhoneData] = useState<any | null>(null);
  const [iraqiPhoneError, setIraqiPhoneError] = useState<string | null>(null);

  const handleVerifyIraqiPhone = async () => {
    if (!patientInfo.phone) {
      setIraqiPhoneError("يرجى إدخال رقم الهاتف أولاً للاستعلام عن دليل الهواتف.");
      return;
    }
    
    setIsVerifyingIraqiPhone(true);
    setIraqiPhoneError(null);
    setIraqiPhoneData(null);
    try {
      addLog("جاري الاتصال بقاعدة بيانات دليل مستخدمي الاتصالات لجمهورية العراق (زين، آسيا، كورك)...");
      const response = await fetch("/api/verify-iraqi-phone", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          phone: patientInfo.phone,
          preferredName: patientInfo.name 
        })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setIraqiPhoneData(result);
        addLog(`تم العثور على المشترك بنجاح عبر شركة الموبايل: ${result.operatorAr}`);
        
        // Auto-fill full name if not yet filled or updated by civil registry
        if (!patientInfo.name && result.subscriberName) {
          setPatientInfo(prev => ({
            ...prev,
            name: result.subscriberName
          }));
        }
      } else {
        setIraqiPhoneError(result.error || "لم نعثر على بيانات مطابقة في دليل هواتف العراق.");
        addLog("فشل العثور على الرقم في الدليل العراقي المشترك.");
      }
    } catch (err) {
      console.error(err);
      setIraqiPhoneError("حدث خطأ أثناء الربط مع بوابات شركات الاتصالات العراقية.");
      addLog("خطأ فني في قنوات الاستعلام الإلكترونية.");
    } finally {
      setIsVerifyingIraqiPhone(false);
    }
  };

  const handleVerifyIraqiId = async () => {
    if (!patientInfo.nationalId) {
      setIraqiVerificationError("يرجى إدخال رقم البطاقة الوطنية الموحدة أولاً لتأكيد التحقق.");
      return;
    }
    const cleanId = patientInfo.nationalId.replace(/[\s-]/g, "");
    if (!/^\d{12}$/.test(cleanId)) {
      setIraqiVerificationError("خطأ: رقم البطاقة الوطنية الموحدة يجب أن يتكون من 12 رقماً عشرياً متتالياً وفقاً لتعليمات مديرية الجنسية العراقية.");
      return;
    }

    setIsVerifyingIraqiId(true);
    setIraqiVerificationError(null);
    setIraqiVerificationData(null);
    try {
      addLog("بدء الاستعلام الفوري من قاعدة بيانات البطاقة الموحدة بجمهورية العراق...");
      const response = await fetch("/api/verify-iraqi-national-id", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          nationalId: cleanId,
          preferredName: patientInfo.name
        })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setIraqiVerificationData(result);
        setPatientInfo(prev => ({
          ...prev,
          name: result.fullName,
          age: (new Date().getFullYear() - new Date(result.dob).getFullYear()).toString()
        }));
        setNameValidationError(null);
        addLog(`تم التحقق بنجاح من الهوية الوطنية للمواطن: ${result.fullName}`);
      } else {
        setIraqiVerificationError(result.error || "فشل النظام في مطابقة رقم البطاقة الوطنية الموحدة. يرجى مراجعة الرقم.");
        addLog("فشل الربط والتحقق مع مركز المعلومات الوطني.");
      }
    } catch (err) {
      console.error(err);
      setIraqiVerificationError("حدث خطأ أثناء الاتصال بقاعدة بيانات البطاقة الموحدة العراقية المؤقتة.");
      addLog("خطأ غير متوقع في قنوات الربط السحابي لمديرية الجنسية.");
    } finally {
      setIsVerifyingIraqiId(false);
    }
  };

  const addLog = (msg: string) => {
    console.log("[Clinical Log]", msg);
  };

  // Player DOM Refs
  const localPlayElRef = useRef<HTMLDivElement>(null);
  const remotePlayElRef = useRef<HTMLDivElement>(null);

  // Load patient preseeded details from session storage if available
  useEffect(() => {
    try {
      const dataStr = sessionStorage.getItem('onlineConsultData');
      if (dataStr) {
        const data = JSON.parse(dataStr);
        setPatientInfo(prev => ({
          ...prev,
          name: data.name || '',
          phone: data.phone || '',
          age: data.age?.toString() || '',
          nationalId: data.nationalId || ''
        }));
      }
    } catch (e) {
      console.error("Error loading session consultation details:", e);
    }
  }, []);

  // Real-time synchronization of Google Meet coordinates and clinical call status (Additive & Secure)
  useEffect(() => {
    if (!createdConsultation?.id) return;

    const socket = connectSocket();

    socket.on("onlineConsultationMeetUrlChanged", (data: { id: string, meetUrl: string }) => {
      if (data.id.toString() === createdConsultation.id.toString()) {
        setCreatedConsultation((prev: any) => {
          if (!prev) return null;
          return { ...prev, meetUrl: data.meetUrl };
        });
        addLog(`[Google Meet] تم تحديث رابط محادثة الاستشارة الحية: ${data.meetUrl}`);
      }
    });

    socket.on("onlineConsultationStatusChanged", (data: { id: string, status: string }) => {
      if (data.id.toString() === createdConsultation.id.toString()) {
        setCreatedConsultation((prev: any) => {
          if (!prev) return null;
          return { ...prev, status: data.status };
        });
        addLog(`[Clinical State] تم نقل حالة الجلسة طبياً إلى: ${data.status}`);
      }
    });

    socket.on("liveDocumentTransferred", (doc: any) => {
      if (doc.consultationId === createdConsultation?.id?.toString() && doc.sender !== 'المراجع') {
        setLiveDocuments(prev => [...prev, doc]);
        addLog(`تم استلام مستند جديد من العيادة: ${doc.name}`);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [createdConsultation?.id]);

  const handleLiveDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
       const newDoc = {
          name: file.name,
          url: reader.result as string,
          sender: 'المراجع',
          consultationId: createdConsultation?.id?.toString()
       };
       const socket = connectSocket();
       socket.emit("liveDocumentTransfer", newDoc);
       setLiveDocuments(prev => [...prev, newDoc]);
       addLog(`تم إرسال مستند: ${file.name}`);
    };
    reader.readAsDataURL(file);
  };

  // Set up the live video call as soon as step is WAITING or VIDEO_CALL
  useEffect(() => {
    if (step !== "VIDEO_CALL") return;

    // Clinical Stability: Under user request, Agora RTC is bypassed. 
    // We seamlessly route patient to immediate Direct Instant Call (Jitsi) or Google Meet.
    addLog("قنوات الاتصال والاستشارة المباشرة جاهزة للتشغيل الآن.");

    return () => {};
  }, [step]);

  const getPaymentReference = () => {
    try {
      if (paymentMethod === "ZAIN_CASH") {
        return `زين كاش: محفظة Payer: ${senderPhoneOrAccount || 'N/A'} - الرقم المرجعي: ${txReference || 'N/A'}`;
      } else if (paymentMethod === "AL_TAIF_BANK") {
        return `مصرف الطيف: اسم المرسل: ${senderName || 'N/A'} - الرقم المرجعي: ${txReference || 'N/A'}`;
      } else if (paymentMethod === "SUPER_QI") {
        return `سوبركي: هاتف الحساب: ${senderPhoneOrAccount || 'N/A'} - الرقم المرجعي: ${txReference || 'N/A'}`;
      } else if (paymentMethod === "QI_CARD") {
        return `كي كارد: رقم الحساب/البطاقة: ${senderPhoneOrAccount || 'N/A'} - اسم المالك: ${senderName || 'N/A'} - الرقم المرجعي: ${txReference || 'N/A'}`;
      }
      return "كوش محدد";
    } catch (e) {
      return "كوش محدد";
    }
  };

  const handlePayment = (e: React.FormEvent) => {
    e.preventDefault();
    setNameValidationError(null);
    setIraqiVerificationError(null);
    setIraqiPhoneError(null);
    setPaymentError(null);

    // 1. Validate Triple Name in Arabic (must consist of at least 3 valid names)
    const nameTrimmed = (patientInfo.name || "").trim();
    const nameParts = nameTrimmed.split(/\s+/).filter(part => part.length >= 2);
    if (nameParts.length < 3) {
      setNameValidationError("تنبيه هام: يرجى كتابة الاسم الثلاثي الصحيح بالكامل باللغة العربية (مثال: أحمد عباس السوداني).");
      return;
    }

    // 2. Validate National ID formatting: must be exactly 12 digits
    const cleanId = (patientInfo.nationalId || "").replace(/[\s-]/g, "");
    if (!/^\d{12}$/.test(cleanId)) {
      setIraqiVerificationError("تنبيه هام: رقم الهوية الوطنية الموحدة يجب أن يتكون من 12 رقماً صحيحاً فقط.");
      return;
    }

    // 3. Validate Iraqi Mobile Phone formatting
    let cleanPhone = (patientInfo.phone || "").trim().replace(/[\s\-\(\)\+]/g, "");
    if (cleanPhone.startsWith("964")) {
      cleanPhone = "0" + cleanPhone.slice(3);
    }
    if (!/^07[5789]\d{8}$/.test(cleanPhone)) {
      setIraqiPhoneError("تنبيه هام: يرجى إدخال رقم هاتف عراقي صحيح يتكون من 11 رقماً ويبدأ بالمفتاح المعتمد (077 للآسياسيل، 078 أو 079 لزين، 075 لكورك).");
      return;
    }

    // 4. Validate Selected Payment Method input values
    if (paymentMethod === "ZAIN_CASH") {
      if (!senderPhoneOrAccount) {
        setPaymentError("يرجى إدخال رقم محفظة زين كاش التي قمت بالتحويل منها.");
        return;
      }
      if (!txReference) {
        setPaymentError("يرجى إدخال الرقم المرجعي للتحويل (Transaction ID).");
        return;
      }
    } else if (paymentMethod === "AL_TAIF_BANK") {
      if (!senderName) {
        setPaymentError("يرجى إدخال اسم مرسل الحوالة بالكامل.");
        return;
      }
      if (!txReference) {
        setPaymentError("يرجى إدخال الرقم المرجعي للحوالة للتأكيد.");
        return;
      }
    } else if (paymentMethod === "SUPER_QI") {
      if (!senderPhoneOrAccount) {
        setPaymentError("يرجى إدخال رقم هاتف حساب سوبركي الذي قمت بالتحويل منه.");
        return;
      }
      if (!txReference) {
        setPaymentError("يرجى إدخال الرقم المرجعي للتحويل لتأكيد العملية.");
        return;
      }
    } else if (paymentMethod === "QI_CARD") {
      if (!senderPhoneOrAccount) {
        setPaymentError("يرجى إدخال آخر 4 أرقام أو رقم حساب الكي كارد الخاص بك.");
        return;
      }
      if (!senderName) {
        setPaymentError("يرجى إدخال الاسم الكامل لمالك بطاقة الكي كارد.");
        return;
      }
      if (!txReference) {
        setPaymentError("يرجى إدخال الرقم المرجعي للحركة المالية لتأكيد الدفع.");
        return;
      }
    }

    // EXPERT COMMENTARY: Directly forward to terms and consent, bypassing the optional and complex image upload step to maximize patient conversion and simplify checkout.
    setStep("TERMS_CONSENT");
  };

  const skipUpload = async () => {
    setStep("WAITING");
    try {
      const reference = getPaymentReference();
      const chosenStr = selectedSlotIndex !== null && availableSlots[selectedSlotIndex]
        ? `\n[توقيت المراجعة المختار بنقرة واحدة بتوقيت العراق: ${availableSlots[selectedSlotIndex].fullMsg}]`
        : "";
      const resp = await fetch('/api/online-consultations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: patientInfo.name,
          phone: patientInfo.phone,
          symptoms: patientInfo.symptoms + chosenStr,
          age: patientInfo.age,
          nationalId: patientInfo.nationalId,
          hasImage: !!uploadedImage,
          paymentMethod,
          paymentReference: reference
        })
      });
      if (resp.ok) {
        const data = await resp.json();
        setCreatedConsultation(data);
      }
    } catch(e) { 
      console.error("Error creating online consultation record:", e);
    }

    // Direct transition to live video interface
    setTimeout(() => {
      setStep("VIDEO_CALL");
    }, 3000);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm rtl-flip font-sans" dir="rtl">
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="bg-indigo-600 text-white p-4 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-500 p-2 rounded-full">
              <Video className="w-5 h-5 text-white animate-pulse" />
            </div>
            <div>
              <h2 className="font-bold text-lg">العيادة الإلكترونية - آي كيو دكتور</h2>
              <p className="text-indigo-200 text-xs text-right">استشارة طبية مرئية فورية وآمنة</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-indigo-500 rounded-full transition text-indigo-100 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          
          {step === "PAYMENT" && (
            <div className="space-y-6 animate-in fade-in duration-300">
              
              {/* Important: Appointment Availability & Busy Status Alert before payment */}
              <div className="bg-amber-50/90 border border-amber-200 rounded-2xl p-5 shadow-xs flex flex-col gap-3.5 text-right">
                <div className="flex items-start gap-2.5">
                  <div className="bg-amber-100 p-2 rounded-xl text-amber-700 shrink-0">
                    <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0" />
                  </div>
                  <div>
                    <h4 className="text-xs font-black text-amber-950 uppercase tracking-wider">💡 تنبيه</h4>
                    <p className="text-[11px] text-amber-900 leading-relaxed font-semibold mt-1">
                      <span className="text-rose-700 font-extrabold block mt-1.5">🚨 في حال لم يبدأ الدوام الرسمي للعيادة بعد، سيتم قبول طلب الحجز الخاص بك وتأكيده تلقائياً، ثُم جدولة الموعد فوراً مع انطلاقة أول شفت تالٍ (الصباحي أو المسائي) للعيادة دون أي إلغاء.</span>
                    </p>
                  </div>
                </div>

                <div className="border-t border-amber-200/50 pt-3">
                  <p className="text-[10.5px] font-extrabold text-[#0f172a] mb-2 flex items-center gap-1.5">
                    🗓 المواعيد المتوفرة القادمة (مرتبة كل 10 دقائق - اختر بنقرة واحدة):
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {availableSlots.length > 0 ? (
                      availableSlots.map((slot, index) => {
                        const isSelected = selectedSlotIndex === index;
                        return (
                          <div 
                            key={slot.id} 
                            onClick={() => {
                              setSelectedSlotIndex(index);
                              playAiSuccessSound();
                            }}
                            onMouseEnter={playAiHoverSound}
                            className={`cursor-pointer rounded-xl p-3 flex flex-col gap-1 text-center transition-all duration-200 shadow-3xs border ${
                              isSelected 
                                ? "bg-indigo-50 border-indigo-600 ring-2 ring-indigo-400" 
                                : "bg-white border-amber-200 hover:bg-amber-100/40"
                            }`}
                          >
                            <span className="text-[10px] font-bold text-slate-500">{slot.label}</span>
                            <span className="text-xs font-black text-[#0f172a]">{slot.time}</span>
                            {isSelected && <span className="text-[8.5px] font-black text-indigo-700 mt-0.5">✓ معتمد بنقرة واحدة</span>}
                          </div>
                        );
                      })
                    ) : (
                      <>
                        <div className="bg-white border border-amber-100 rounded-lg p-2.5 flex flex-col gap-0.5 text-center shadow-3xs animate-pulse">
                          <span className="text-[10px] font-bold text-slate-400">جاري الحساب...</span>
                          <span className="text-[11.5px] font-black text-slate-500">+10 دقائق</span>
                        </div>
                        <div className="bg-white border border-amber-100 rounded-lg p-2.5 flex flex-col gap-0.5 text-center shadow-3xs animate-pulse">
                          <span className="text-[10px] font-bold text-slate-400">جاري الحساب...</span>
                          <span className="text-[11.5px] font-black text-slate-500">+20 دقيقة</span>
                        </div>
                        <div className="bg-white border border-amber-100 rounded-lg p-2.5 flex flex-col gap-0.5 text-center shadow-3xs animate-pulse">
                          <span className="text-[10px] font-bold text-slate-400">جاري الحساب...</span>
                          <span className="text-[11.5px] font-black text-slate-500">+30 دقيقة</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-white/80 border border-amber-200/30 rounded-xl p-3 text-[10.5px] leading-relaxed text-slate-700 font-semibold">
                  🔹 <span className="text-amber-950 font-black">حجز موعدك بسهولة:</span> لتجنب فترات الانتظار الطويلة وتثبيت موعدك القادم تلقائياً وبأولية تامة، نوصيك بشدة بالتوجه فوراً للاستعانة بـ <strong className="text-indigo-900 font-bold">مساعدة الاستعلامات (آلاء)</strong> لحجز وتأكيد موعدك المناسب في ثوانٍ!
                </div>

                {onOpenChat && (
                  <button
                    type="button"
                    onClick={onOpenChat}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold py-2.5 px-4 rounded-xl text-xs flex items-center justify-center gap-2 transition cursor-pointer select-none"
                  >
                    <MessageCircle className="w-4 h-4 text-indigo-200 animate-bounce" />
                    <span>💬 الانتقال إلى مساعدة الاستعلامات (آلاء) لحجز موعدك بسهولة الآن</span>
                  </button>
                )}
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center">
                <div className="inline-flex bg-indigo-100 text-indigo-700 p-3 rounded-full mb-4">
                  <CreditCard className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">رسوم الاستشارة الإلكترونية</h3>
                <div className="text-3xl font-black text-indigo-600 mb-4">10,000 د.ع</div>
                <p className="text-slate-500 text-sm max-w-md mx-auto leading-relaxed">
                  الاستشارة تشمل مكالمة فيديو حية مباشرة مع الطبيب، إمكانية رفع صور الحالة، والحصول على الوصفة الطبية إلكترونياً.
                </p>
              </div>

              <form onSubmit={handlePayment} className="space-y-4 max-w-lg mx-auto bg-slate-50/50 p-5 rounded-2xl border border-slate-100 shadow-xs">
                {/* 1. National Unified ID Card Section */}
                <div className="space-y-2">
                  <label className="block text-xs font-bold text-slate-700">رقم البطاقة الوطنية الموحدة العراقي (١٢ رقم)</label>
                  <input 
                    required 
                    type="text" 
                    value={patientInfo.nationalId} 
                    onChange={e => {
                      const val = e.target.value.replace(/[^\d]/g, ""); // Permit digits only
                      setPatientInfo({...patientInfo, nationalId: val.slice(0, 12)});
                    }} 
                    className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 font-mono tracking-wider text-center" 
                    placeholder="مثال: 198510204050" 
                    maxLength={12}
                  />

                  {/* Iraqi National Database Verification - Error alert */}
                  {iraqiVerificationError && (
                    <div className="bg-rose-50 border border-rose-150 rounded-lg p-3 text-right flex items-start gap-2 text-rose-800 animate-in fade-in duration-200">
                      <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                      <div className="text-[10px] leading-normal font-semibold">
                        {iraqiVerificationError}
                      </div>
                    </div>
                  )}
                </div>

                {/* 2. Full Triple Name Field */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">الاسم الثلاثي الصحيح باللغة العربية</label>
                  <input 
                    required 
                    type="text" 
                    value={patientInfo.name} 
                    onChange={e => {
                      setPatientInfo({...patientInfo, name: e.target.value});
                      setNameValidationError(null);
                    }} 
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" 
                    placeholder="الاسم الأول + اسم الأب + اسم الجد (مثال: مصطفى علي الجبوري)" 
                  />
                  {nameValidationError && (
                    <p className="text-[10px] text-rose-600 font-bold mt-1 text-right">{nameValidationError}</p>
                  )}
                </div>

                {/* 3. Age & Phone Number fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">العمر</label>
                    <input required type="number" value={patientInfo.age} onChange={e => setPatientInfo({...patientInfo, age: e.target.value})} className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" placeholder="مثال: 35" />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-xs font-bold text-slate-700">رقم الهاتف الجوال العراقي</label>
                    <input 
                      required 
                      type="tel" 
                      value={patientInfo.phone} 
                      onChange={e => {
                        setPatientInfo({...patientInfo, phone: e.target.value});
                        setIraqiPhoneError(null);
                      }} 
                      className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-left font-mono" 
                      placeholder="07XXXXXXXX" 
                      dir="ltr"
                    />

                    {/* Carrier Verification Error State */}
                    {iraqiPhoneError && (
                      <div className="bg-rose-50 border border-rose-150 rounded-lg p-2.5 text-right flex items-start gap-2 text-rose-800 animate-in fade-in duration-200">
                        <ShieldAlert className="w-3.5 h-3.5 text-rose-600 shrink-0 mt-0.5" />
                        <div className="text-[10px] leading-normal font-semibold">
                          {iraqiPhoneError}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* 4. Symptoms text areas */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">نبذة عن الحالة والأعراض</label>
                  <textarea rows={3} value={patientInfo.symptoms} onChange={e => setPatientInfo({...patientInfo, symptoms: e.target.value})} className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" placeholder="صف الأعراض والعلامات التي تشعر بها لمساعدتنا في تحري التشخيص الطبي..." />
                </div>

                {/* Iraqi Payment Method Selector */}
                <div className="border-t border-slate-200/80 pt-4 mt-2 space-y-3">
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-xs font-black text-indigo-950">طريقة دفع رسوم الفحص (١٠,٠٠٠ د.ع)</label>
                    <span className="bg-rose-500 text-white font-extrabold text-[9px] px-2 py-0.5 rounded-full animate-pulse">فوري وآمن</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => { setPaymentMethod("ZAIN_CASH"); setPaymentError(null); }}
                      className={`py-2 px-1.5 rounded-xl border text-[10px] font-black flex flex-col items-center justify-center gap-1 transition-all cursor-pointer ${
                        paymentMethod === "ZAIN_CASH"
                          ? "border-emerald-500 bg-emerald-50/50 text-emerald-950 ring-2 ring-emerald-500/20"
                          : "border-slate-200 bg-white hover:bg-slate-50 text-slate-600"
                      }`}
                    >
                      <span className="text-base">📱</span>
                      <span>زين كاش</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => { setPaymentMethod("AL_TAIF_BANK"); setPaymentError(null); }}
                      className={`py-2 px-1.5 rounded-xl border text-[10px] font-black flex flex-col items-center justify-center gap-1 transition-all cursor-pointer ${
                        paymentMethod === "AL_TAIF_BANK"
                          ? "border-indigo-500 bg-indigo-50/50 text-indigo-950 ring-2 ring-indigo-500/20"
                          : "border-slate-200 bg-white hover:bg-slate-50 text-slate-600"
                      }`}
                    >
                      <span className="text-base">🏦</span>
                      <span>مصرف الطيف</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => { setPaymentMethod("SUPER_QI"); setPaymentError(null); }}
                      className={`py-2 px-1.5 rounded-xl border text-[10px] font-black flex flex-col items-center justify-center gap-1 transition-all cursor-pointer ${
                        paymentMethod === "SUPER_QI"
                          ? "border-amber-500 bg-amber-50/50 text-amber-950 ring-2 ring-amber-500/20"
                          : "border-slate-200 bg-white hover:bg-slate-50 text-slate-600"
                      }`}
                    >
                      <span className="text-base">✨</span>
                      <span>سوبركي (SuperQi)</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => { setPaymentMethod("QI_CARD"); setPaymentError(null); }}
                      className={`py-2 px-1.5 rounded-xl border text-[10px] font-black flex flex-col items-center justify-center gap-1 transition-all cursor-pointer ${
                        paymentMethod === "QI_CARD"
                          ? "border-rose-500 bg-rose-50/50 text-rose-950 ring-2 ring-rose-500/20"
                          : "border-slate-200 bg-white hover:bg-slate-50 text-slate-600"
                      }`}
                    >
                      <span className="text-base">💳</span>
                      <span>كي كارد (Qi Card)</span>
                    </button>
                  </div>

                  {/* Payment Method Details container */}
                  <div className="bg-slate-150/70 border border-slate-200/60 rounded-xl p-3.5 space-y-3 mt-2 text-right">
                    {paymentMethod === "ZAIN_CASH" && (
                      <div className="space-y-3 text-right text-xs">
                        <p className="font-semibold text-slate-600 leading-relaxed text-[11px]">
                          يرجى تحويل مبلغ <strong className="text-emerald-700">١٠,٠٠٠ دينار عراقي</strong> إلى رقم محفظة زين كاش المعتمدة:
                        </p>
                        <div className="bg-white border border-slate-200 rounded-lg py-2 px-3 text-center flex items-center justify-between">
                          <span className="text-slate-400 font-bold text-[10px]">Zain Cash Wallet</span>
                          <span className="font-mono text-base font-bold text-emerald-600 select-all">9647869392828</span>
                        </div>
                        <div className="space-y-2 pt-1.5 border-t border-dashed border-slate-300">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-600 mb-1">رقم المحفظة التي قمت بالتحويل منها</label>
                            <input
                              type="text"
                              required={paymentMethod === "ZAIN_CASH"}
                              value={senderPhoneOrAccount}
                              onChange={e => setSenderPhoneOrAccount(e.target.value.replace(/[^\d]/g, ""))}
                              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs text-left font-mono"
                              placeholder="07XXXXXXXX"
                              dir="ltr"
                              maxLength={15}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-600 mb-1">الرقم المرجعي للتحويل (Transaction ID)</label>
                            <input
                              type="text"
                              required={paymentMethod === "ZAIN_CASH"}
                              value={txReference}
                              onChange={e => setTxReference(e.target.value.trim())}
                              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs text-center font-mono"
                              placeholder="مثال: 948194"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {paymentMethod === "AL_TAIF_BANK" && (
                      <div className="space-y-3 text-right text-xs">
                        <p className="font-semibold text-slate-600 leading-relaxed text-[11px]">
                          يرجى تحويل مبلغ <strong className="text-indigo-700">١٠,٠٠٠ دينار عراقي</strong> إلى حساب مصرف الطيف عن طريق الهاتف المعتمد:
                        </p>
                        <div className="bg-white border border-slate-200 rounded-lg py-2 px-3 text-center flex items-center justify-between">
                          <span className="text-slate-400 font-bold text-[10px]">Al-Taif Bank (Phone No.)</span>
                          <span className="font-mono text-base font-bold text-indigo-600 select-all">07869392828</span>
                        </div>
                        <div className="space-y-2 pt-1.5 border-t border-dashed border-slate-300">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-600 mb-1">اسم مرسل الحوالة بالكامل</label>
                            <input
web-search                              type="text"
                              required={paymentMethod === "AL_TAIF_BANK"}
                              value={senderName}
                              onChange={e => setSenderName(e.target.value)}
                              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs text-right"
                              placeholder="الاسم الكامل كما يظهر في تطبيق المصرف"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-600 mb-1">الرقم المرجعي للحوالة / رقم الإشعار</label>
                            <input
                              type="text"
                              required={paymentMethod === "AL_TAIF_BANK"}
                              value={txReference}
                              onChange={e => setTxReference(e.target.value.trim())}
                              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs text-center font-mono"
                              placeholder="الرقم المرجعي أو كود المعاملة"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {paymentMethod === "SUPER_QI" && (
                      <div className="space-y-3 text-right text-xs">
                        <p className="font-semibold text-slate-600 leading-relaxed text-[11px]">
                          يرجى تحويل مبلغ <strong className="text-amber-700">١٠,٠٠٠ دينار عراقي</strong> إلى رقم هاتف حساب سوبركي المعتمد:
                        </p>
                        <div className="bg-white border border-slate-200 rounded-lg py-2 px-3 text-center flex items-center justify-between">
                          <span className="text-slate-400 font-bold text-[10px]">SuperQi Phone Account</span>
                          <span className="font-mono text-base font-bold text-amber-600 select-all">07869392828</span>
                        </div>
                        <div className="space-y-2 pt-1.5 border-t border-dashed border-slate-300">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-600 mb-1">رقم هاتف حساب سوبركي الذي قمت بالتحويل منه</label>
                            <input
                              type="text"
                              required={paymentMethod === "SUPER_QI"}
                              value={senderPhoneOrAccount}
                              onChange={e => setSenderPhoneOrAccount(e.target.value.replace(/[^\d]/g, ""))}
                              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs text-left font-mono"
                              placeholder="07XXXXXXXX"
                              dir="ltr"
                              maxLength={15}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-600 mb-1">الرقم المرجعي للتحويل</label>
                            <input
                              type="text"
                              required={paymentMethod === "SUPER_QI"}
                              value={txReference}
                              onChange={e => setTxReference(e.target.value.trim())}
                              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs text-center font-mono"
                              placeholder="كود التأكيد أو الرقم المرجعي للعملية"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {paymentMethod === "QI_CARD" && (
                      <div className="space-y-3 text-right text-xs">
                        <p className="font-semibold text-slate-600 leading-relaxed text-[11px]">
                          يرجى تحويل مبلغ <strong className="text-rose-700">١٠,٠٠٠ دينار عراقي</strong> إلى رقم حساب الكي كارد المعتمد:
                        </p>
                        <div className="bg-white border border-slate-200 rounded-lg py-2 px-3 text-center flex items-center justify-between">
                          <span className="text-slate-400 font-bold text-[10px]">Qi Card Account No.</span>
                          <span className="font-mono text-base font-bold text-rose-600 select-all">3968065593</span>
                        </div>
                        <div className="space-y-2 pt-1.5 border-t border-dashed border-slate-300">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-600 mb-1">رقم حساب الكي كارد الذي قمت بالتحويل منه (أو أول 6 أو آخر 4 أرقام)</label>
                            <input
                              type="text"
                              required={paymentMethod === "QI_CARD"}
                              value={senderPhoneOrAccount}
                              onChange={e => setSenderPhoneOrAccount(e.target.value.replace(/[^\d]/g, ""))}
                              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs text-left font-mono"
                              placeholder="رقم حسابك أو آخر 4 أرقام من البطاقة"
                              dir="ltr"
                              maxLength={16}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-600 mb-1">الاسم الكامل لمالك بطاقة الكي كارد</label>
                            <input
                              type="text"
                              required={paymentMethod === "QI_CARD"}
                              value={senderName}
                              onChange={e => setSenderName(e.target.value)}
                              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs text-right"
                              placeholder="اسم مالي البطاقة كما في هويته"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-600 mb-1">الرقم المرجعي للحركة المالية (Transaction Ref)</label>
                            <input
                              type="text"
                              required={paymentMethod === "QI_CARD"}
                              value={txReference}
                              onChange={e => setTxReference(e.target.value.trim())}
                              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs text-center font-mono"
                              placeholder="رقم العملية المكون من عدة أرقام"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {paymentError && (
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-2.5 text-right flex items-start gap-2 text-rose-800 animate-in fade-in duration-200">
                      <ShieldAlert className="w-3.5 h-3.5 text-rose-600 shrink-0 mt-0.5" />
                      <div className="text-[10px] leading-normal font-bold">
                        {paymentError}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Submit action button */}
                <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-4 rounded-lg shadow-md transition-colors flex items-center justify-center gap-2 mt-4 cursor-pointer select-none">
                  متابعة ودفع الرسوم <CreditCard className="w-4 h-4" />
                </button>
              </form>

              {/* FAQ Section Trigger Link (Additive Only) */}
              <div className="text-center pt-3 border-t border-dashed border-slate-200/80 mt-4 max-w-lg mx-auto">
                <button
                  type="button"
                  onClick={() => setShowFAQ(true)}
                  className="inline-flex items-center justify-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 font-bold transition-colors cursor-pointer"
                >
                  <MessageCircle className="w-4 h-4 text-indigo-500" />
                  <span>💬 هل لديك أسئلة؟ اقرأ اللوائح والأسئلة الشائعة حول الطب الاتصالي بالعراق</span>
                </button>
              </div>
            </div>
          )}

          {step === "UPLOAD" && (
            <div className="space-y-6 text-center animate-in fade-in duration-300">
              <h3 className="text-xl font-bold text-slate-800">مرفقات الاستشارة</h3>
              <p className="text-slate-500 text-sm max-w-md mx-auto leading-relaxed">
                لكي يحصل الطبيب على صورة أوضح للحالة، يمكنك تصوير مكان الألم، الجروح، أو التقارير الطبية السابقة.
              </p>
              
              <div className="border-2 border-dashed border-indigo-200 bg-indigo-50/50 rounded-2xl p-8 max-w-md mx-auto relative group">
                <input type="file" accept="image/*" onChange={handleImageUpload} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                
                {!uploadedImage ? (
                  <div className="flex flex-col items-center justify-center gap-4">
                    <div className="bg-white p-4 rounded-full shadow-sm">
                      <Camera className="w-8 h-8 text-indigo-400" />
                    </div>
                    <div>
                      <p className="font-bold text-indigo-700">اضغط لالتقاط أو رفع صورة</p>
                      <p className="text-xs text-slate-400 mt-1">تنسيقات الصور المدعومة: JPG, PNG</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <div className="relative w-40 h-40 rounded-xl overflow-hidden shadow-md border-4 border-white mb-4">
                      <img src={uploadedImage} alt="مرفق المريض" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                         <p className="text-white text-xs font-bold">تغيير الصورة</p>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-indigo-700 mb-2">تم رفع المرفق بنجاح</p>
                  </div>
                )}
              </div>

              <div className="flex justify-center gap-4 max-w-md mx-auto mt-8">
                <button type="button" onClick={() => setStep("TERMS_CONSENT")} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 cursor-pointer">
                  تخطي والدخول للعيادة
                </button>
                <button type="button" onClick={() => setStep("TERMS_CONSENT")} disabled={!uploadedImage} className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg shadow-md transition-colors flex items-center justify-center gap-2 cursor-pointer">
                  تأكيد واستمرار
                </button>
              </div>
            </div>
          )}

          {step === "TERMS_CONSENT" && (
            <div className="space-y-5 animate-in fade-in duration-300">
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 text-right space-y-5 shadow-sm max-h-[60vh] overflow-y-auto">
                <div className="flex items-center gap-2.5 border-b border-slate-100 pb-3.5">
                  <FileText className="w-5 h-5 text-indigo-600" />
                  <div>
                    <h3 className="font-extrabold text-[#1E293B] text-sm">
                      إخلاء مسؤولية وإقرار موافقة لخدمات الاستشارة الطبية عن بُعد
                    </h3>
                    <p className="text-[10px] text-slate-400 mt-1">
                      مرحباً بك في خدمة الاستشارات الطبية عن بُعد. حرصاً منا على تقديم أفضل خدمة صحية وضمان سلامتك، يُرجى قراءة البنود التالية والموافقة عليها قبل البدء بالاستشارة:
                    </p>
                  </div>
                </div>

                {/* 1. ليست للحالات الطارئة */}
                <div className="bg-rose-50 border border-rose-200 p-4 rounded-xl flex gap-3 text-right">
                  <div className="text-rose-600 font-extrabold text-lg leading-none mt-0.5 shrink-0">🚨</div>
                  <div className="space-y-1">
                    <h4 className="font-extrabold text-rose-950 text-xs text-rose-800">
                      1. ليست للحالات الطارئة
                    </h4>
                    <p className="text-[#7F1D1D] text-[11px] leading-relaxed font-semibold">
                      هذه الخدمة غير مخصصة للحالات الطارئة أو المهددة للحياة. إذا كنت تعاني من حالة طبية طارئة (مثل: ألم شديد في الصدر، صعوبة في التنفس، نزيف حاد، فقدان للوعي، أو إصابات خطيرة)، يرجى التوجه فوراً إلى أقرب مستشفى طوارئ أو الاتصال بالإسعاف الفوري في العراق على الرقم (122) او الرقم (911).
                    </p>
                  </div>
                </div>

                {/* Other Terms list in detailed style */}
                <div className="space-y-4 text-xs text-slate-700 leading-relaxed">
                  
                  {/* 2. طبيعة الاستشارة وحدودها */}
                  <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-100">
                    <h5 className="font-extrabold text-[#1E293B] text-xs flex items-center gap-1.5 mb-1.5">
                      <span className="w-5 h-5 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center font-bold text-[10px]">٢</span>
                      طبيعة الاستشارة وحدودها
                    </h5>
                    <nav className="space-y-2 pr-5 list-disc text-slate-600 text-[11px]">
                      <li>
                        <strong>بديل مؤقت وليس نهائياً:</strong> الاستشارة عن بُعد (عبر الفيديو، الصوت، أو المحادثة النصية) تهدف إلى تقديم التوجيه الطبي، التقييم المبدئي، والمتابعة، مراجعة الفحوصات، الاستشارات العامة والأمراض الجلدية ولكنها لا تغني بأي حال من الأحوال عن الفحص السريري المباشر في العيادة.
                      </li>
                      <li>
                        <strong>التشخيص المبدئي:</strong> التشخيص المقدم عبر الإنترنت يعتمد كلياً على المعلومات والأعراض التي تصفها، وقد يكون غير مكتمل لعدم القدرة على إجراء فحص جسدي شامل أو أخذ العلامات الحيوية بشكل مباشر.
                      </li>
                    </nav>
                  </div>

                  {/* 3. مسؤولية المريض عن المعلومات */}
                  <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-100">
                    <h5 className="font-extrabold text-[#1E293B] text-xs flex items-center gap-1.5 mb-1.5">
                      <span className="w-5 h-5 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center font-bold text-[10px]">٣</span>
                      مسؤولية المريض عن المعلومات
                    </h5>
                    <p className="text-slate-600 text-[11px] pr-2">
                      أنت مسؤول عن تقديم معلومات طبية دقيقة وكاملة عن حالتك الصحية، تاريخك المرضي، والأدوية التي تتناولها حالياً وحساسية الأدوية.
                      أي نقص أو تضليل في المعلومات المقدمة قد يؤدي إلى تقييم طبي غير دقيق، والطبيب المعالج يخلي مسؤوليته عن أي مضاعفات تنتج عن إخفاء معلومات جوهرية.
                    </p>
                  </div>

                  {/* 4. الوصفات الطبية والعلاجات */}
                  <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-100">
                    <h5 className="font-extrabold text-[#1E293B] text-xs flex items-center gap-1.5 mb-1.5">
                      <span className="w-5 h-5 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center font-bold text-[10px]">٤</span>
                      الوصفات الطبية والعلاجات
                    </h5>
                    <p className="text-slate-600 text-[11px] pr-2">
                      يتم صرف الوصفات الطبية عبر هذه الخدمة وفقاً لتقدير الطبيب السريري وبما يتماشى مع ضوابط وتعليمات وزارة الصحة العراقية ونقابة أطباء العراق.
                      يُمنع منعاً باتاً وصف الأدوية الخاضعة للرقابة (مثل المهدئات، المنومات، والمسكنات الأفيونية) عبر الاستشارات عن بُعد، ويجب مراجعة العيادة الخاصة للحصول عليها.
                    </p>
                  </div>

                  {/* 5. الخصوصية والقيود التقنية */}
                  <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-100">
                    <h5 className="font-extrabold text-[#1E293B] text-xs flex items-center gap-1.5 mb-1.5">
                      <span className="w-5 h-5 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center font-bold text-[10px]">٥</span>
                      الخصوصية والقيود التقنية
                    </h5>
                    <p className="text-slate-600 text-[11px] pr-2">
                      نحن نلتزم بحماية بياناتك الصحية وتشفيرها قدر الإمكان، ولكنك تقر بأن التواصل عبر الإنترنت يحمل دائماً نسبة من المخاطر التقنية (مثل انقطاع الاتصال المفاجئ أو المشاكل التقنية الخارجة عن إرادتنا) التي قد تؤثر على جودة أو استمرارية الاستشارة.
                    </p>
                  </div>

                </div>

                
                {/* 6. غير مسموح بتسجيل محادثة الفيديو */}
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl flex gap-3 text-right mt-4">
                  <div className="text-amber-600 font-extrabold text-lg leading-none mt-0.5 shrink-0">📵</div>
                  <div className="space-y-1">
                    <h4 className="font-extrabold text-amber-950 text-xs text-amber-800">
                      6. يمنع تسجيل محادثة الفيديو
                    </h4>
                    <p className="text-[#92400E] text-[11px] leading-relaxed font-semibold">
                      يُمنع بتاتاً تسجيل مكالمة الفيديو من قِبَل المُراجع أو التقاط صور للشاشة، وذلك التزاماً بتعليمات الخصوصية وسرية الجلسة الطبية.
                    </p>
                  </div>
                </div>

                {/* Explicit Checkbox representing compliance validation */}
                <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl flex items-start gap-3 mt-4 select-none">
                  <input
                    type="checkbox"
                    id="iraqi-telehealth-consent-checkbox"
                    checked={acceptedIraqiTerms}
                    onChange={(e) => setAcceptedIraqiTerms(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 mt-0.5 cursor-pointer ml-1"
                  />
                  <label 
                    htmlFor="iraqi-telehealth-consent-checkbox" 
                    className="text-indigo-950 text-xs leading-relaxed cursor-pointer font-bold"
                  >
                    7. الإقرار والموافقة: باستخدامك لهذه الخدمة، فإنك تقر بأنك قد قرأت وفهمت جميع البنود المذكورة أعلاه، وتوافق على تلقي الرعاية الطبية عن بُعد مع إدراكك الكامل لحدودها مقارنة بالزيارة الحضورية للعيادة.
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 max-w-md mx-auto mt-6">
                <button
                  type="button"
                  onClick={() => setStep("PAYMENT")}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-1 cursor-pointer"
                >
                  الرجوع للخلف
                </button>
                <button
                  type="button"
                  onClick={skipUpload}
                  disabled={!acceptedIraqiTerms}
                  className="flex-1 bg-indigo-600 hover:bg-[#0F172A] disabled:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <CheckCircle className="w-4 h-4 ml-1 shrink-0" />
                  أوافق والبدء بالاستشارة
                </button>
              </div>
            </div>
          )}

          {step === "WAITING" && (
            <div className="flex flex-col items-center justify-center h-64 animate-in fade-in duration-300">
              <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-6"></div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">جاري حجز غرفتك في خوادم الفيديو...</h3>
              <p className="text-slate-500 text-sm">سيتم فتح مكالمة الفيديو المباشرة وبانتظار الطبيب المرخص مباشرةً.</p>
            </div>
          )}

          {step === "VIDEO_CALL" && (
            <div className="space-y-4 animate-in fade-in zoom-in-95 duration-500 flex flex-col h-full">
              
              <div className="flex items-center justify-between bg-emerald-600 text-white px-4 py-2.5 rounded-t-xl">
                 <div className="flex items-center gap-2">
                   <div className="w-2.5 h-2.5 bg-emerald-300 rounded-full animate-pulse"></div>
                   <span className="text-xs font-sans font-bold block">بوابة استشارة Google Meet الآمنة</span>
                 </div>
                 <div className="text-xs font-bold font-sans">
                   رقم الجلسة: {createdConsultation?.id || "غرفة عامة"}
                 </div>
              </div>

              {/* Main Call Action Container offering both Direct Instant Call and Google Meet */}
              {showEmbeddedVideo ? (
                <div className="w-full flex-1 flex flex-col bg-slate-950 overflow-hidden rounded-b-xl border border-slate-800 relative shadow-xl">
                  <div className="bg-slate-900 border-b border-slate-800 px-4 py-3 flex justify-between items-center text-white shrink-0">
                    <div className="flex items-center gap-2">
                      <span className="bg-indigo-500/10 p-1.5 rounded-full border border-indigo-500/20">
                        <Video className="w-4 h-4 text-indigo-400 animate-pulse" />
                      </span>
                      <span className="text-[11px] font-bold text-indigo-300">مكالمة الفيديو الطبي المباشر (مدمجة وبدون ضرورة لتنزيل أي تطبيق)</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowEmbeddedVideo(false)}
                      className="bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white px-3 py-1.5 rounded-lg text-[10px] font-bold transition cursor-pointer border border-slate-700"
                    >
                      الرجوع وخيارات الاتصال
                    </button>
                  </div>
                  <div className="w-full h-[525px] md:h-[625px] relative bg-slate-900">
                    <iframe
                      src={`https://meet.jit.si/IQDoctor-${createdConsultation?.id}#config.disableDeepLinking=true&config.prejoinPageEnabled=false&interfaceConfig.MOBILE_APP_PROMO=false&config.enableWelcomePage=false`}
                      allow="camera; microphone; display-capture; autoplay"
                      className="w-full h-full absolute inset-0 border-0"
                      title="Patient Video Call"
                    />
                  </div>
                </div>
              ) : (
                <div className="bg-slate-900 text-white rounded-b-xl p-6 flex flex-col items-center justify-center text-center space-y-5 border-2 border-slate-800 shadow-xl min-h-[350px]">
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-105 transition">
                  <Video className="w-10 h-10 text-indigo-600" />
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-slate-100">مرحباً {patientInfo.name || "المراجع الكريم"}</h3>
                  <p className="text-slate-400 text-xs max-w-sm mx-auto leading-relaxed">
                    اختر أحد الخيارات لتأمين مكالمتك واستشارتك الطبية المباشرة مع الطبيب فوراً:
                  </p>
                </div>

                <div className="w-full max-w-md bg-slate-950 p-5 rounded-xl border border-slate-800 space-y-4">
                  {/* Option 1: Direct Instant Video Call - Jitsi Meet (100% Guaranteed to work instantly, no codes/accounts error!) */}
                  <div className="border border-indigo-500/30 bg-indigo-500/5 p-3.5 rounded-lg text-right space-y-2.5">
                    <div className="flex items-center justify-between">
                      <span className="bg-indigo-600 text-white text-[9px] font-extrabold px-2 py-0.5 rounded-full">
                        مستحسن ومضمون للربط الفوري (دون تنزيل تطبيقات)
                      </span>
                      <h4 className="font-bold text-xs text-indigo-200">الخيار الأول: اتصال طبي فوري وبسيط</h4>
                    </div>
                    <p className="text-[10px] text-indigo-300 leading-normal">
                      لا يتطلب هذا الاتصال أي بريد إلكتروني أو تنزيل تطبيق. يفتح مباشرة داخل صفحتك الحالية ويمنع مشاكل "Check your meeting code" تماماً.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        playAiSuccessSound();
                        setShowEmbeddedVideo(true);
                      }}
                      className="w-full bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white font-extrabold py-2.5 px-4 rounded-lg shadow-md hover:shadow-indigo-500/25 transition-all duration-300 flex items-center justify-center gap-1.5 text-xs cursor-pointer"
                    >
                      <Video className="w-4 h-4 ml-1" />
                      بدء الاتصال المباشر داخل الصفحة فوراً (دون مغادرة) 💻
                    </button>
                    <a
                      href={`https://meet.jit.si/IQDoctor-${createdConsultation?.id}#config.disableDeepLinking=true&config.prejoinPageEnabled=false&interfaceConfig.MOBILE_APP_PROMO=false&config.enableWelcomePage=false`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-center bg-white/10 hover:bg-white/20 text-indigo-200 py-2 rounded-lg text-[10px] font-sans mt-2 border border-white/10 transition-all font-bold"
                    >
                      🔗 اضغط هنا لفتح المحادثة في المتصفح مباشرة (إذا لم تعمل الكاميرا أعلاه)
                    </a>
                  </div>

                  {/* Option 2: WhatsApp (Failover) */}
                  <div className="border border-emerald-900/30 bg-emerald-900/10 p-3.5 rounded-lg text-right space-y-2.5">
                    <h4 className="font-bold text-xs text-emerald-200">الخيار الثاني: تواصل واتساب (في حال فشل المحادثة)</h4>
                    <p className="text-[10px] text-emerald-300 leading-normal">
                      في حال فشل الاتصال المباشر أعلاه، يرجى التواصل فوراً مع الاستقبال عبر واتساب لطلب الدعم الفني أو متابعة الاستشارة:
                    </p>
                    <div className="flex flex-col gap-2">
                      <SendWhatsAppButton 
                        patientPhone={patientInfo.phone || "9647833075240"} 
                        messageContent={`🩺 [طلب مساعدة تقنية] واجهت مشكلة في المحادثة المرئية Jitsi للمريض ${patientInfo.name || ""} (رقم المحادثة: ${createdConsultation?.id || ""}). يرجى المساعدة.`}
                        label="إشعار الدعم الفني تلقائياً ومباشرة"
                      />
                      <a
                        href={`https://wa.me/9647833075240?text=${encodeURIComponent(`مرحباً الدكتور واثق الجلبي، لدي مشكلة في الاتصال بمحادثة الفيديو Jitsi للاستشارة رقم ${createdConsultation?.id || ""}. يرجى المتابعة والدعم.`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-[#25D366] hover:bg-[#1EBE5D] text-white font-extrabold py-2 px-4 rounded-lg shadow-sm transition-all duration-300 flex items-center justify-center gap-1.5 text-xs cursor-pointer"
                      >
                        <MessageCircle className="w-4 h-4" />
                        مراسلة الاستقبال والدعم الفني يدوياً
                      </a>
                    </div>
                  </div>
                </div>

{/* Option 3: WhatsApp Text Consultation Option */}
                <div className="bg-emerald-500/10 border border-emerald-500/20 p-3.5 rounded-lg text-right space-y-2.5 w-full max-w-md">
                  <h4 className="font-bold text-xs text-emerald-400">الخيار الثالث: استشارة عبر واتساب (رسائل فقط)</h4>
                  <p className="text-[10px] text-slate-400 leading-normal">
                    إذا كنت لا ترغب في إجراء مكالمة فيديو وتفضل التواصل النصي وإرسال التقارير، يمكنك تحويل استشارتك إلى محادثة واتساب مع الطبيب.
                  </p>
                  <a
                    href="https://wa.me/9647833075240?text=%D9%85%D8%B1%D8%AD%D8%A8%D8%A7%D9%8B%2C%20%D9%84%D9%82%D8%AF%20%D9%82%D9%85%D8%AA%20%D8%A8%D8%AF%D9%81%D8%B9%20%D8%B1%D8%B3%D9%88%D9%85%20%D8%A7%D9%84%D8%A7%D8%B3%D8%AA%D8%B4%D8%A7%D8%B1%D8%A9%20%D8%A7%D9%84%D9%85%D8%B1%D8%A6%D9%8A%D8%A9%20%D9%88%D8%A3%D8%B1%D8%BA%D8%A8%20%D8%A8%D8%A5%D9%83%D9%85%D8%A7%D9%84%20%D8%A7%D9%84%D8%A7%D8%B3%D8%AA%D8%B4%D8%A7%D8%B1%D8%A9%20%D8%B9%D8%A8%D8%B1%20%D8%A7%D9%84%D9%88%D8%A7%D8%AA%D8%B3%D8%A7%D8%A8."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-[#25D366] hover:bg-[#1EBE5D] text-white font-extrabold py-2 px-4 rounded-lg shadow-sm transition-all duration-300 flex items-center justify-center gap-1.5 text-xs cursor-pointer"
                  >
                    <MessageCircle className="w-4 h-4 ml-1" />
                    استشارة محادثة بدون فيديو (واتساب)
                  </a>
                </div>

                {/* Patient Guidance Tip Box */}
                <div className="bg-amber-500/10 border border-amber-500/20 text-right p-3.5 rounded-lg space-y-1 w-full max-w-md">
                  <h4 className="font-bold text-[11px] text-amber-400 flex items-center gap-1.5 justify-end">
                    ℹ️ إرشاد طبي هام لسلامة الاتصال
                  </h4>
                  <p className="text-[10px] text-slate-400 leading-relaxed font-sans">
                    إذا نقرت على زر Google Meet وظهرت لك رسالة خطأ (Check your meeting code)، فهذا يعني أن الطبيب لم يبدأ الجلسة بعد في حساب غوغل عيادته. ننصحك بالضغط على <strong>"الخيار الأول: اتصال طبي مباشر فوري"</strong> في الأعلى للدخول مباشرة واختصار الوقت!
                  </p>
                </div>

                {/* Tips & Guidance Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-right w-full max-w-md pt-2">
                  <div className="bg-slate-950/40 p-3 rounded-lg border border-slate-800/40">
                    <h5 className="font-bold text-[11px] text-zinc-300 mb-1">🎙️ نقاء صوت وصورة ممتاز</h5>
                    <p className="text-[10px] text-slate-500 leading-normal">تأكد من تفعيل الميكروفون ومنح متصفحك الإذن في علامة التبويب الجديدة فور دخولك.</p>
                  </div>
                  <div className="bg-slate-950/40 p-3 rounded-lg border border-slate-800/40">
                    <h5 className="font-bold text-[11px] text-zinc-300 mb-1">📹 تجهيز مكان إضاءة جيد</h5>
                    <p className="text-[10px] text-slate-500 leading-normal">اجلس في هدوء كامل ليتمكن الطبيب من سماعك وتشخيص شكواك بدقة كاملة.</p>
                  </div>
                </div>
              </div>
            )}

                {/* Instant Document Transfer Panel */}
                <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
                  <div className="bg-slate-100 border-b border-slate-200 px-4 py-2 flex justify-between items-center text-right">
                    <h5 className="font-bold text-xs text-slate-700 flex items-center gap-1.5 basis-full justify-start">
                      <FileText className="w-4 h-4 text-emerald-600" />
                      تبادل المستندات مع الطبيب
                    </h5>
                    <label className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 py-1 px-3 rounded shadow-sm text-xs font-bold cursor-pointer shrink-0 transition flex items-center gap-1">
                      <Upload className="w-3.5 h-3.5" />
                      إرسال ملف
                      <input type="file" className="hidden" accept="image/*,.pdf,.doc,.docx" onChange={handleLiveDocumentUpload} />
                    </label>
                  </div>
                  <div className="p-4 bg-slate-50 flex flex-col gap-2 max-h-40 overflow-y-auto min-h-[60px]">
                    {liveDocuments.length === 0 ? (
                      <div className="text-center text-slate-400 text-xs italic my-auto">
                        لا توجد ملفات متبادلة في هذه الجلسة.
                      </div>
                    ) : (
                      liveDocuments.map((doc, i) => (
                        <a key={i} href={doc.url} download={doc.name} className="flex justify-between items-center p-2 rounded-lg bg-white border border-slate-200 hover:shadow-sm transition">
                          <span className="text-[11px] text-slate-400">{doc.sender === 'المراجع' ? 'أنت' : 'العيادة (الطبيب)'}: </span>
                          <span className="text-xs font-bold text-indigo-700 text-left truncate ltr flex-1 max-w-[150px] mx-2">{doc.name}</span>
                          <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-1 flex items-center gap-1 rounded font-bold hover:bg-slate-200 shrink-0">
                            تحميل
                          </span>
                        </a>
                      ))
                    )}
                  </div>
                </div>

              {/* Back controls */}
              <div className="flex justify-center mt-2">
                <button 
                  onClick={onClose} 
                  className="px-6 py-2.5 bg-slate-700 hover:bg-slate-800 text-white rounded-full shadow-md text-xs font-bold transition flex items-center gap-2 cursor-pointer"
                >
                  <PhoneOff className="w-3.5 h-3.5 ml-0.5 animate-pulse" />
                  إنهاء العيادة والرجوع للرئيسية
                </button>
              </div>

            </div>
          )}
          
        </div>

        {/* FAQ Panel Overlay (Additive Only) */}
        {showFAQ && (
          <div className="absolute inset-0 z-40 bg-slate-50 flex flex-col animate-in slide-in-from-bottom duration-300">
            {/* Header */}
            <div className="bg-indigo-700 text-white p-4 flex justify-between items-center shrink-0 shadow-sm">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-300 animate-pulse" />
                <div className="text-right">
                  <h3 className="font-bold text-sm">الأسئلة الشائعة ولوائح الطب الاتصالي بالعراق</h3>
                  <p className="text-[10px] text-indigo-200">إجابات فورية موثوقة ومدعومة ببحث غوغل المباشر</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowFAQ(false);
                  setFaqAnswer(null);
                  setFaqSources([]);
                  setFaqQuestion("");
                }} 
                className="p-1.5 px-3 bg-indigo-800/80 hover:bg-indigo-600 rounded-lg text-xs font-bold text-indigo-100 hover:text-white transition flex items-center gap-1 cursor-pointer"
              >
                <span>الرجوع للاستمارة</span>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Body Content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5 text-right">
              <div className="bg-gradient-to-l from-indigo-50 to-blue-50 border border-indigo-100/85 rounded-2xl p-4">
                <h4 className="font-bold text-xs text-indigo-950 mb-1 flex items-center gap-1.5 leading-normal justify-start">
                  <span>💡 بوابة المعلومات الطبية واللوائح في العراق</span>
                </h4>
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  اختر أحد الأسئلة الشائعة أدناه أو اكتب استفسارك الخاص، وسيقوم نظام الذكاء الاصطناعي بالبحث الفوري وتوفير الإجابة بدقة بالغة مع ذكر الروابط والمصادر المرجعية المعتمدة بجمهورية العراق.
                </p>
              </div>

              {/* Predefined Questions Grid */}
              <div className="space-y-2">
                <h5 className="text-[11px] font-black text-slate-800 pr-1">أسئلة شائعة تهمك:</h5>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    "هل استشارة الطبيب ومكالمة الفيديو مرخصة وقانونية بالعراق؟",
                    "ما هي طرق دفع الرسوم المتاحة (زين كاش وغيرها) وكيف أحصل على وصفتي؟",
                    "هل يحمي نظام آي كيو دكتور بياناتي وهويتي الوطنية وخصوصية الاتصال لوزارة الصحة؟",
                    "ما الذي يجب عليّ فعله قبل بدء مكالمة الفيديو مع الطبيب الاستشاري؟"
                  ].map((qst, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleFetchFAQ(qst)}
                      className="w-full text-right p-3 rounded-xl border border-slate-200/60 bg-white hover:border-indigo-400 hover:bg-slate-50 transition text-xs font-semibold text-slate-700 leading-normal flex justify-between items-center group cursor-pointer"
                    >
                      <span className="group-hover:text-indigo-900 transition">{qst}</span>
                      <span className="text-slate-400 group-hover:text-indigo-500 font-bold mr-2 text-base">←</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Ask input field */}
              <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs">
                <h5 className="text-[11px] font-bold text-slate-800 mb-2 pr-0.5">اسأل الذكاء الاصطناعي سؤالاً مخصصاً:</h5>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={faqQuestion}
                    onChange={(e) => setFaqQuestion(e.target.value)}
                    placeholder="مثال: هل الطب الاتصالي معتمد لنقابة أطباء العراق؟"
                    className="flex-1 bg-slate-50 border border-slate-300 rounded-xl px-4 py-2.5 text-xs outline-none focus:border-indigo-500 focus:bg-white text-right"
                  />
                  <button
                    type="button"
                    onClick={() => handleFetchFAQ(faqQuestion)}
                    disabled={faqLoading || !faqQuestion.trim()}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition disabled:opacity-50 flex items-center gap-1.5 shrink-0 select-none cursor-pointer"
                  >
                    {faqLoading ? "جاري البحث..." : "بحث وإجابة"}
                  </button>
                </div>
              </div>

              {/* Loading Indicator */}
              {faqLoading && (
                <div className="flex flex-col items-center justify-center p-8 gap-3 animate-pulse">
                  <div className="w-8 h-8 rounded-full border-2 border-indigo-600 border-t-transparent animate-spin" />
                  <p className="text-[11px] font-bold text-indigo-700">جاري قراءة لوائح الطب الاتصالي وإجراء بحث غوغل المباشر...</p>
                </div>
              )}

              {/* Text Answer Display */}
              {faqAnswer && !faqLoading && (
                <div className="bg-white border border-indigo-200 rounded-2xl p-5 shadow-xs space-y-4 text-right animate-in fade-in duration-300">
                  <div className="flex justify-between items-center border-b border-slate-100 pb-2.5">
                    <span className="text-xs font-bold text-slate-500">سؤالك: "{faqQuestion}"</span>
                    <span className="bg-indigo-100 text-indigo-700 font-bold text-[9px] px-2 py-0.5 rounded-md">إجابة معتمدة جغرافياً</span>
                  </div>
                  
                  <p className="text-xs text-slate-700 leading-relaxed font-normal whitespace-pre-wrap">{faqAnswer}</p>
                  
                  {faqSources.length > 0 && (
                    <div className="pt-3 border-t border-dashed border-slate-200 space-y-2">
                      <p className="text-[10px] font-bold text-slate-500">🔗 مصادر موثوقة ومراجعة إلكترونية لغوغل للبحث:</p>
                      <div className="flex flex-wrap gap-2">
                        {faqSources.map((src, sIdx) => (
                          <a
                            key={sIdx}
                            href={src.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 bg-indigo-50 hover:bg-indigo-150 border border-indigo-100/50 rounded-lg px-2.5 py-1 text-[10px] font-medium text-indigo-700 transition"
                          >
                            <span>{src.title}</span>
                            <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
